import {
  Timestamp,
  collection,
  doc,
  getDoc,
  getDocs,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import { db } from './firebase';

export type ResetScheduleMode = 'daily' | 'weekly';
export type ProductResetSelectionMode = 'all' | 'only_selected' | 'all_except_selected';

export interface ProductAvailabilityResetGlobalConfig {
  enabled: boolean;
  defaultStoreEnabled: boolean;
  excludePromoProducts: boolean;
  scheduleMode: ResetScheduleMode;
  weeklyDay: number;
  hour: number;
  timeZone: string;
  updatedAt?: Date;
}

export interface ProductAvailabilityResetStoreConfig {
  storeId: string;
  enabled?: boolean;
  selectionMode: ProductResetSelectionMode;
  productIds: string[];
  updatedAt?: Date;
}

export interface ProductAvailabilityResetRuntimeState {
  lastRunKey?: string | null;
  lastRunAt?: Date;
  lastError?: string | null;
  lastErrorAt?: Date;
  updatedCount?: number;
  skippedPromoCount?: number;
  affectedStoreCount?: number;
  updatedAt?: Date;
}

const GLOBAL_CONFIG_REF = doc(db, 'config', 'product_availability_reset');
const STORE_CONFIGS_REF = collection(db, 'product_availability_reset_stores');
const RUNTIME_STATE_REF = doc(db, 'product_availability_reset_state', 'runtime');

export const DEFAULT_PRODUCT_AVAILABILITY_RESET_GLOBAL_CONFIG: ProductAvailabilityResetGlobalConfig = {
  enabled: false,
  defaultStoreEnabled: true,
  excludePromoProducts: true,
  scheduleMode: 'weekly',
  weeklyDay: 2,
  hour: 5,
  timeZone: 'Africa/Casablanca',
};

function toDate(value: unknown): Date | undefined {
  return value instanceof Timestamp ? value.toDate() : undefined;
}

function clampHour(value: unknown): number {
  const parsed = Number(value);
  if (Number.isNaN(parsed)) {
    return DEFAULT_PRODUCT_AVAILABILITY_RESET_GLOBAL_CONFIG.hour;
  }
  return Math.min(23, Math.max(0, Math.trunc(parsed)));
}

function clampWeekday(value: unknown): number {
  const parsed = Number(value);
  if (Number.isNaN(parsed)) {
    return DEFAULT_PRODUCT_AVAILABILITY_RESET_GLOBAL_CONFIG.weeklyDay;
  }
  return Math.min(6, Math.max(0, Math.trunc(parsed)));
}

function sanitizeScheduleMode(value: unknown): ResetScheduleMode {
  return value === 'daily' ? 'daily' : 'weekly';
}

function sanitizeSelectionMode(value: unknown): ProductResetSelectionMode {
  if (value === 'only_selected' || value === 'all_except_selected') {
    return value;
  }
  return 'all';
}

export async function getProductAvailabilityResetGlobalConfig(): Promise<ProductAvailabilityResetGlobalConfig> {
  const snap = await getDoc(GLOBAL_CONFIG_REF);
  if (!snap.exists()) {
    return { ...DEFAULT_PRODUCT_AVAILABILITY_RESET_GLOBAL_CONFIG };
  }

  const data = snap.data() || {};
  return {
    enabled: data.enabled === true,
    defaultStoreEnabled:
      typeof data.defaultStoreEnabled === 'boolean'
        ? data.defaultStoreEnabled
        : DEFAULT_PRODUCT_AVAILABILITY_RESET_GLOBAL_CONFIG.defaultStoreEnabled,
    excludePromoProducts:
      typeof data.excludePromoProducts === 'boolean'
        ? data.excludePromoProducts
        : DEFAULT_PRODUCT_AVAILABILITY_RESET_GLOBAL_CONFIG.excludePromoProducts,
    scheduleMode: sanitizeScheduleMode(data.scheduleMode),
    weeklyDay: clampWeekday(data.weeklyDay),
    hour: clampHour(data.hour),
    timeZone:
      typeof data.timeZone === 'string' && data.timeZone.trim().length > 0
        ? data.timeZone.trim()
        : DEFAULT_PRODUCT_AVAILABILITY_RESET_GLOBAL_CONFIG.timeZone,
    updatedAt: toDate(data.updatedAt),
  };
}

export async function updateProductAvailabilityResetGlobalConfig(
  partial: Partial<ProductAvailabilityResetGlobalConfig>
): Promise<ProductAvailabilityResetGlobalConfig> {
  const payload: Record<string, unknown> = {
    updatedAt: serverTimestamp(),
  };

  if (partial.enabled !== undefined) payload.enabled = partial.enabled === true;
  if (partial.defaultStoreEnabled !== undefined) {
    payload.defaultStoreEnabled = partial.defaultStoreEnabled === true;
  }
  if (partial.excludePromoProducts !== undefined) {
    payload.excludePromoProducts = partial.excludePromoProducts === true;
  }
  if (partial.scheduleMode !== undefined) {
    payload.scheduleMode = sanitizeScheduleMode(partial.scheduleMode);
  }
  if (partial.weeklyDay !== undefined) {
    payload.weeklyDay = clampWeekday(partial.weeklyDay);
  }
  if (partial.hour !== undefined) {
    payload.hour = clampHour(partial.hour);
  }
  if (partial.timeZone !== undefined) {
    payload.timeZone =
      typeof partial.timeZone === 'string' && partial.timeZone.trim().length > 0
        ? partial.timeZone.trim()
        : DEFAULT_PRODUCT_AVAILABILITY_RESET_GLOBAL_CONFIG.timeZone;
  }

  await setDoc(GLOBAL_CONFIG_REF, payload, { merge: true });
  return getProductAvailabilityResetGlobalConfig();
}

export async function listProductAvailabilityResetStoreConfigs(): Promise<ProductAvailabilityResetStoreConfig[]> {
  const snapshot = await getDocs(STORE_CONFIGS_REF);
  return snapshot.docs.map((docSnap) => {
    const data = docSnap.data() || {};
    return {
      storeId:
        typeof data.storeId === 'string' && data.storeId.trim().length > 0
          ? data.storeId.trim()
          : docSnap.id,
      enabled: typeof data.enabled === 'boolean' ? data.enabled : undefined,
      selectionMode: sanitizeSelectionMode(data.selectionMode),
      productIds: Array.isArray(data.productIds)
        ? data.productIds
            .filter((value): value is string => typeof value === 'string')
            .map((value) => value.trim())
            .filter((value) => value.length > 0)
        : [],
      updatedAt: toDate(data.updatedAt),
    };
  });
}

export async function getProductAvailabilityResetStoreConfig(
  storeId: string
): Promise<ProductAvailabilityResetStoreConfig | null> {
  const ref = doc(db, 'product_availability_reset_stores', storeId);
  const snap = await getDoc(ref);
  if (!snap.exists()) {
    return null;
  }

  const data = snap.data() || {};
  return {
    storeId,
    enabled: typeof data.enabled === 'boolean' ? data.enabled : undefined,
    selectionMode: sanitizeSelectionMode(data.selectionMode),
    productIds: Array.isArray(data.productIds)
      ? data.productIds
          .filter((value): value is string => typeof value === 'string')
          .map((value) => value.trim())
          .filter((value) => value.length > 0)
      : [],
    updatedAt: toDate(data.updatedAt),
  };
}

export async function updateProductAvailabilityResetStoreConfig(
  storeId: string,
  partial: Partial<ProductAvailabilityResetStoreConfig>
): Promise<ProductAvailabilityResetStoreConfig> {
  const payload: Record<string, unknown> = {
    storeId,
    updatedAt: serverTimestamp(),
  };

  if (partial.enabled !== undefined) {
    payload.enabled = partial.enabled === true;
  }
  if (partial.selectionMode !== undefined) {
    payload.selectionMode = sanitizeSelectionMode(partial.selectionMode);
  }
  if (partial.productIds !== undefined) {
    payload.productIds = partial.productIds
      .filter((value): value is string => typeof value === 'string')
      .map((value) => value.trim())
      .filter((value) => value.length > 0);
  }

  await setDoc(doc(db, 'product_availability_reset_stores', storeId), payload, { merge: true });
  return (await getProductAvailabilityResetStoreConfig(storeId)) as ProductAvailabilityResetStoreConfig;
}

export async function getProductAvailabilityResetRuntimeState(): Promise<ProductAvailabilityResetRuntimeState | null> {
  const snap = await getDoc(RUNTIME_STATE_REF);
  if (!snap.exists()) {
    return null;
  }

  const data = snap.data() || {};
  return {
    lastRunKey: typeof data.lastRunKey === 'string' ? data.lastRunKey : null,
    lastRunAt: toDate(data.lastRunAt),
    lastError: typeof data.lastError === 'string' ? data.lastError : null,
    lastErrorAt: toDate(data.lastErrorAt),
    updatedCount: typeof data.updatedCount === 'number' ? data.updatedCount : undefined,
    skippedPromoCount:
      typeof data.skippedPromoCount === 'number' ? data.skippedPromoCount : undefined,
    affectedStoreCount:
      typeof data.affectedStoreCount === 'number' ? data.affectedStoreCount : undefined,
    updatedAt: toDate(data.updatedAt),
  };
}
